import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from './shared/utils/api';
import { formatDate } from './shared/utils/dateUtils';

function scoreBadgeClass(pct) {
  if (pct == null) return 'ims-score-none';
  if (pct >= 70) return 'ims-score-green';
  if (pct >= 40) return 'ims-score-amber';
  return 'ims-score-red';
}

// Header is defined outside ImportSummary so React doesn't treat it as a new
// component type on every render (which would force DOM unmount/remount).
function Header() {
  return (
    <header className="ims-header">
      <div className="ims-header-inner">
        <a href="/app" className="ims-brand">
          <svg viewBox="0 0 64 64" width="28" height="28" aria-hidden="true">
            <polygon points="6,54 24,54 34,30 16,30" fill="#ffffff" opacity="0.3"/>
            <polygon points="20,46 38,46 48,22 30,22" fill="#ffffff" opacity="0.6"/>
            <polygon points="34,38 52,38 62,14 44,14" fill="#ffffff" opacity="0.95"/>
          </svg>
          <span className="ims-wordmark"><span className="ims-wm-sqe">SQE</span><span className="ims-wm-track">Track</span></span>
        </a>
      </div>
    </header>
  );
}

function ShimmerLoading() {
  return (
    <>
      <div className="ims-shimmer-card">
        <div className="ims-shimmer ims-shimmer-block ims-shimmer-wide" />
        <div className="ims-shimmer ims-shimmer-block ims-shimmer-med" />
        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          {[1,2,3].map(i => (
            <div key={i} className="ims-shimmer" style={{ flex: 1, height: 80, borderRadius: 12 }} />
          ))}
        </div>
      </div>
      <div className="ims-shimmer-card">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="ims-shimmer ims-shimmer-block" style={{ width: `${80 - i * 8}%` }} />
        ))}
      </div>
    </>
  );
}

function OverrideSelect({ currentValue, allL2s, onChange }) {
  const grouped = useMemo(() => (
    Object.entries(
      allL2s.reduce((acc, l) => {
        const key = `${l.module} › ${l.topic}`;
        (acc[key] = acc[key] || []).push(l);
        return acc;
      }, {})
    )
  ), [allL2s]);

  return (
    <select
      className="ims-override-select"
      value={currentValue || ''}
      onChange={e => onChange(e.target.value || null)}
    >
      <option value="">— Reset to auto-match —</option>
      {grouped.map(([groupLabel, items]) => (
        <optgroup key={groupLabel} label={groupLabel}>
          {items.map(l => (
            <option key={l.general_topic} value={l.general_topic}>{l.general_topic}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default function ImportSummary() {
  const params   = new URLSearchParams(window.location.search);
  const importId = params.get('id');
  const hasToken = !!localStorage.getItem('sqetrack_token');

  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [batch,      setBatch]      = useState(null);
  const [topics,     setTopics]     = useState([]);
  const [unmatched,  setUnmatched]  = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [allL2s,     setAllL2s]     = useState([]);
  const [edits,      setEdits]      = useState({});    // { result_id: newGT } for matched
  const [assignments,setAssignments]= useState({});    // { source_label: newGT } for unmatched
  const [editingId,  setEditingId]  = useState(null);  // result_id currently showing dropdown
  const [saving,     setSaving]     = useState(false);
  const [saveMsg,    setSaveMsg]    = useState(null);
  const savingRef   = useRef(false);    // guards against double-submit
  const saveMsgTimer = useRef(null);

  const load = useCallback(async () => {
    if (!importId) { setError('no-id'); setLoading(false); return; }
    try {
      const data = await api(`/api/import/lawdrills/${importId}`);
      if (data.error) {
        setError(data.error === 'Import not found' ? 'not-found' : 'server');
      } else {
        setBatch(data.batch);
        setTopics(data.topics || []);
        setUnmatched(data.unmatched || []);
        setSummary(data.summary || null);
        setAllL2s(data.all_l2s || []);
      }
    } catch {
      setError('network');
    }
    setLoading(false);
  }, [importId]);

  useEffect(() => { load(); }, [load]);

  // Clean up any pending save-message timer on unmount
  useEffect(() => () => { if (saveMsgTimer.current) clearTimeout(saveMsgTimer.current); }, []);

  const pendingCount = Object.keys(edits).length + Object.keys(assignments).length;

  const handleSave = useCallback(async () => {
    if (!pendingCount || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveMsg(null);
    try {
      const overridesList = Object.entries(edits).map(([result_id, sqetrack_general_topic]) => ({
        result_id: parseInt(result_id, 10),
        sqetrack_general_topic: sqetrack_general_topic || null,
      }));
      const assignmentsList = Object.entries(assignments).map(([source_label, sqetrack_general_topic]) => ({
        source_label,
        batch_id: batch.id,
        sqetrack_general_topic,
      }));
      const data = await api('/api/import/lawdrills/override', {
        method: 'POST',
        body: JSON.stringify({ overrides: overridesList, assignments: assignmentsList }),
      });
      if (data.error) throw new Error(data.error);
      setSaveMsg('Corrections saved');
      setEdits({});
      setAssignments({});
      setEditingId(null);
      saveMsgTimer.current = setTimeout(() => setSaveMsg(null), 3000);
      await load();
    } catch (e) {
      setSaveMsg(`Could not save corrections: ${e.message}`);
    }
    savingRef.current = false;
    setSaving(false);
  }, [edits, assignments, batch, pendingCount, load]);

  // ── Render states ──────────────────────────────────────────────────────────

  if (!hasToken) return (
    <div className="ims-page">
      <Header />
      <div className="ims-content">
        <div className="ims-state-card">
          <div className="ims-state-title">Log in to view your results</div>
          <div className="ims-state-body">Log in to SQETrack to view import results.</div>
          <a className="ims-cta-btn" href="/login">Log in to SQETrack</a>
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div className="ims-page">
      <Header />
      <div className="ims-content"><ShimmerLoading /></div>
    </div>
  );

  if (error === 'not-found') return (
    <div className="ims-page">
      <Header />
      <div className="ims-content">
        <div className="ims-state-card">
          <div className="ims-state-title">Import not found</div>
          <div className="ims-state-body">This import may have been replaced by a newer one.</div>
          <a className="ims-cta-btn" href="/app">Go to dashboard</a>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="ims-page">
      <Header />
      <div className="ims-content">
        <div className="ims-state-card">
          <div className="ims-state-title">Could not load this import</div>
          <div className="ims-state-body">{
            error === 'no-id' ? 'No import ID was provided in the URL.' :
            'Check your connection and try again.'
          }</div>
          <button className="ims-cta-btn" onClick={() => { setError(null); setLoading(true); load(); }}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );

  const unmatchedCount = summary?.topics_unmatched ?? unmatched.length;

  return (
    <div className="ims-page">
      <Header />
      <div className="ims-content">

        <a className="ims-back" href="/app">← Back to dashboard</a>

        {/* ── Summary card ── */}
        <div className="ims-summary-card">
          <div className="ims-title">
            <svg className="ims-title-check" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <circle cx="14" cy="14" r="14" fill="#DCFCE7"/>
              <path d="M8 14l4.5 4.5L20 8" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Import complete
          </div>

          {summary && (
            <div className="ims-stats">
              <div className="ims-stat-box">
                <div className="ims-stat-num">{summary.topics_matched}</div>
                <div className="ims-stat-label">topics matched</div>
              </div>
              <div className="ims-stat-box">
                <div className="ims-stat-num">{summary.subtopics_updated}</div>
                <div className="ims-stat-label">subtopics updated</div>
              </div>
              <div className="ims-stat-box">
                <div className="ims-stat-num">{summary.questions_imported}</div>
                <div className="ims-stat-label">questions imported</div>
              </div>
            </div>
          )}

          <div className="ims-meta-line">
            Law Drills · {batch?.paper} · {formatDate(batch?.imported_at)}
          </div>

          <a className="ims-cta-btn" href="/app">View dashboard</a>

          {unmatchedCount > 0 && (
            <div className="ims-unmatched-notice">
              {unmatchedCount} topic{unmatchedCount !== 1 ? 's' : ''} could not be matched. Review them below.
            </div>
          )}
        </div>

        {/* ── Detail card ── */}
        <div className="ims-detail-card">
          <div className="ims-detail-heading">Imported results</div>

          {topics.length > 0 && (
            <table className="ims-table">
              <thead>
                <tr>
                  <th className="ims-td-ld">Law Drills topic</th>
                  <th className="ims-td-score">Score</th>
                  <th className="ims-td-sqe">SQETrack topic</th>
                  <th className="ims-td-status">Status</th>
                </tr>
              </thead>
              <tbody>
                {topics.map(t => {
                  const isEdited   = edits.hasOwnProperty(t.id);
                  const currentGT  = isEdited ? edits[t.id] : t.sqetrack_general_topic;
                  const isEditing  = editingId === t.id;
                  const pct        = t.score_pct != null ? Math.round(t.score_pct) : null;

                  return (
                    <tr key={t.id}>
                      <td className="ims-td-ld">
                        {t.l3_source_labels?.length > 0
                          ? t.l3_source_labels.join(' · ')
                          : t.source_label}
                      </td>
                      <td className="ims-td-score">
                        <span className={`ims-score-badge ${scoreBadgeClass(pct)}`}>
                          {pct != null ? `${pct}%` : '—'}
                        </span>
                      </td>
                      <td className="ims-td-sqe">
                        {isEditing ? (
                          <OverrideSelect
                            currentValue={currentGT}
                            allL2s={allL2s}
                            onChange={newGT => {
                              setEdits(prev => ({ ...prev, [t.id]: newGT }));
                              setEditingId(null);
                            }}
                          />
                        ) : (
                          <>
                            <div className="ims-sqe-name">{currentGT || '—'}</div>
                            {t.attempted && (
                              <div className="ims-sqe-sub">{t.attempted}q imported</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="ims-td-status">
                        {isEdited
                          ? <span className="ims-status-edited">✎ Edited</span>
                          : <span className="ims-status-matched">✓ Matched</span>
                        }
                        {!isEditing && (
                          <button className="ims-change-link" onClick={() => setEditingId(t.id)}>
                            Change
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {unmatched.length > 0 && (
            <>
              <div className="ims-sub-heading">{unmatched.length} unmatched topic{unmatched.length !== 1 ? 's' : ''}</div>
              <div className="ims-sub-hint">These Law Drills topics could not be matched to SQETrack topics. Assign the correct topic or skip them.</div>
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-td-ld">Law Drills topic</th>
                    <th className="ims-td-score">Score</th>
                    <th className="ims-td-sqe">Assign to SQETrack topic</th>
                    <th className="ims-td-status">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatched.map(label => {
                    const assigned = assignments.hasOwnProperty(label);
                    return (
                      <tr key={label}>
                        <td className="ims-td-ld">{label}</td>
                        <td className="ims-td-score"><span className="ims-score-badge ims-score-none">—</span></td>
                        <td className="ims-td-sqe">
                          <OverrideSelect
                            currentValue={assignments[label] || null}
                            allL2s={allL2s}
                            onChange={newGT => {
                              if (!newGT) {
                                setAssignments(prev => { const n = {...prev}; delete n[label]; return n; });
                              } else {
                                setAssignments(prev => ({ ...prev, [label]: newGT }));
                              }
                            }}
                          />
                        </td>
                        <td className="ims-td-status">
                          {assigned
                            ? <span className="ims-status-assigned">✎ Assigned</span>
                            : <span className="ims-status-unmatched">Unmatched</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          <div className="ims-actions">
            {saveMsg && (
              <span className={saveMsg.startsWith('Could not save') ? 'ims-status-edited' : 'ims-save-confirm'}>
                {saveMsg}
              </span>
            )}
            <button
              className="ims-save-btn"
              disabled={!pendingCount || saving}
              onClick={handleSave}
            >
              {saving ? 'Saving' : `Save corrections${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
